import Cookies from 'js-cookie';
import { NextRequest, NextResponse } from 'next/server';
import type CurrencyCode from '@alltrails/types/CurrencyCode';
import { getPurchaseEventId, getSignupEventId, getTrialStartEventId } from './conversionsApi';
import { marketingCookieAttributes, NINETY_DAYS_MS } from './marketingCookies';

const CLICK_ID_QUERY_PARAM_NAME = 'rdt_cid';
export const CLICK_ID_COOKIE_NAME = '_rdt_cid';

// https://business.reddithelp.com/s/article/manual-conversion-events-with-the-reddit-pixel
export const trackPurchaseForReddit = ({
  userId,
  currencyCode,
  value,
  isTrial,
  subscriptionEventId
}: {
  userId: number;
  currencyCode: CurrencyCode;
  value: number;
  isTrial: boolean;
  subscriptionEventId?: number;
}) => {
  if (isRedditPixelUnavailable() || !subscriptionEventId) {
    return;
  }

  const externalId = userId.toString();

  if (isTrial) {
    rdt('track', 'Custom', {
      customEventName: 'TrialStart',
      conversionId: getTrialStartEventId(subscriptionEventId),
      externalId
    });
  } else {
    rdt('track', 'Purchase', {
      currency: currencyCode,
      value,
      conversionId: getPurchaseEventId(subscriptionEventId),
      externalId
    });
  }
};

export const trackCompleteRegistrationForReddit = (userId: number, currencyCode?: CurrencyCode | null) => {
  if (isRedditPixelUnavailable()) {
    return;
  }

  rdt('track', 'SignUp', { conversionId: getSignupEventId(userId), externalId: userId.toString(), currency: currencyCode || 'USD', value: 0 });
};

export const setRedditCookiesMiddleware = (request: NextRequest, response: NextResponse) => {
  const { searchParams } = request.nextUrl;
  const redditClickIdParam = searchParams.get(CLICK_ID_QUERY_PARAM_NAME);
  const ninetyDaysFromNow = new Date(Date.now() + NINETY_DAYS_MS);

  if (redditClickIdParam) {
    response.cookies.set({
      name: CLICK_ID_COOKIE_NAME,
      value: redditClickIdParam,
      expires: ninetyDaysFromNow,
      domain: process.env.APEX_DOMAIN!,
      secure: true
    });
  }
};

/**
 * Client-side counterpart to `setRedditCookiesMiddleware`, writing `_rdt_cid`.
 *
 * @param apexDomain - Apex domain to scope the cookie to, so it is readable across subdomains.
 */
export const setRedditCookiesClientSide = (apexDomain: string): void => {
  const redditClickIdParam = new URLSearchParams(window.location.search).get(CLICK_ID_QUERY_PARAM_NAME);

  if (redditClickIdParam) {
    Cookies.set(CLICK_ID_COOKIE_NAME, redditClickIdParam, marketingCookieAttributes(apexDomain));
  }
};

// Check for presence of rdt in case of content blockers
const isRedditPixelUnavailable = () => {
  return typeof rdt === 'undefined';
};
